/**
 * Where a reader got to in a document, following them between devices.
 *
 * A document is read over days and on more than one machine, and the two things a
 * card needs to offer a read back are *where the reader stopped* and *how much of it
 * is done*. Neither is in a play: a submission is the pages that asked something and
 * were answered, which says nothing about the pages that asked nothing — most of a
 * document — and nothing at all about a reader who has read nine pages and answered
 * none of them.
 *
 * So this is the same bargain the FlashBook's place makes
 * (`#/lib/flashbookProgressSync`), and it is deliberately the same shape: one record
 * per document, in the reader's *own* repo, at a key both devices compute from the
 * document's AT-URI. Private, because a reading position is nobody else's business
 * and nothing on the AppView needs it; portable, because the next device is by
 * definition the one that has not been used yet; and written through the PDS's own
 * record API, which is both the shortest path and the only one that works on a
 * deployment whose AppView has not been taught about documents at all.
 *
 * The record carries the pages as well as the counts, and for the reason the pages
 * are the interesting half: `answered` can be counted from submissions, but the pages
 * exist *only* here, and a card that had to fetch a document's material and rebuild a
 * session to say "nine of sixteen read" would be a card that shows nothing until a
 * fetch it does not need lands. The *pages* rather than a bare count, because a count
 * cannot be carried: a second sitting opens in the middle of the document, so it knows
 * two new pages where the record knew nine, and a count would have to choose between
 * the two — while the union of the two lists is exactly what was read. The count stays
 * in the record because it is what a reader is shown and what a build that predates the
 * list can still write.
 *
 * It is read defensively, and not out of habit: the record is written by a different
 * install of the app, which may be an older one or a newer one, and a resumer that
 * trusted it would take a reader to a page that is not a page.
 *
 * A read that has *ended* leaves no place: a finished document has nothing to carry
 * on with, so a place at the document's own last page is one `documentResume` ignores
 * rather than one anybody clears. (A record is never deleted: a card cannot tell a
 * finished read from a reader who went back to page 1, and the two would have the same
 * thing to say next time.)
 */
import {
  type DocumentPlaySession,
  documentReadCounts,
  documentReadPages,
  stoppedPageOf,
} from '#/lib/document/report'
import {progressRecordKey} from '#/lib/flashbookProgressSync'

/**
 * The collection a document place lives in.
 *
 * A record type of the app's own in the reader's repo, exactly as
 * `app.bsky.feed.flashbookProgress` is — and the two never collide, because they
 * are different collections: the key only has to be unique within one of them.
 */
export const DOCUMENT_PROGRESS_COLLECTION = 'app.bsky.feed.documentProgress'

/**
 * How many pages a record will carry.
 *
 * A document long enough to reach this is a document nobody reads to the end in one
 * place, and a record is written on every page turn: the list is bounded so that a
 * pathological document cannot make the writes grow without limit. A document at the
 * bound keeps counting pages beyond it (`pagesSeen`), which is the one number that can
 * still grow.
 */
export const DOCUMENT_PAGES_LIMIT = 1000

/** Where a reader got to, as one document's record holds it. */
export type DocumentPlace = {
  /** The page they stopped on — the last page this read brought into view. */
  page: number
  /**
   * The pages of the document this reader has read, in order and without repeats.
   *
   * The whole point of the record: two sittings are one read, and only the pages
   * themselves can say how much of the document the two amount to.
   */
  pages: number[]
  /** How many pages of the document the read has brought into view. */
  pagesSeen: number
  /** How many questions the read has answered, across every page answered. */
  answered: number
  /** When this place was last written, so a reader can be told how stale it is. */
  updatedAt: string
}

/** The record, as it sits in the repo. */
export type DocumentProgressRecord = {
  $type: typeof DOCUMENT_PROGRESS_COLLECTION
  /** The document this is the place *in*, so a record can be checked against the ask. */
  documentUri: string
  /** The title as it was when the place was last written, so it can name itself. */
  documentTitle?: string
  updatedAt: string
  page: number
  pages?: number[]
  pagesSeen: number
  answered: number
}

/**
 * The pages a place has read, out of any number of sources.
 *
 * Every list that goes in is defensive — a record is written by another install of the
 * app, which may be an older one or a newer one — so anything that is not a page number
 * is dropped rather than trusted, and the result is in order, without repeats, and no
 * longer than `DOCUMENT_PAGES_LIMIT`.
 */
export function mergeDocumentPages(
  ...lists: readonly (readonly unknown[] | undefined | null)[]
): number[] {
  const pages = new Set<number>()
  for (const list of lists) {
    for (const page of list ?? []) {
      if (typeof page === 'number' && Number.isInteger(page) && page > 0) {
        pages.add(page)
      }
    }
  }
  return [...pages].sort((a, b) => a - b).slice(0, DOCUMENT_PAGES_LIMIT)
}

/**
 * The key one document's place is kept at.
 *
 * The app's one rule for where a read's place lives, shared with the FlashBook's place
 * rather than re-spelled here: an rkey is unique only within one repo, so the key has
 * to be derived from the *whole* AT-URI (see `progressRecordKey`), and two documents
 * by two authors that happen to share an rkey must not share a place.
 */
export function documentProgressRecordKey(documentUri: string): string {
  return progressRecordKey(documentUri)
}

/**
 * The place a read in progress amounts to, or `null` when there is none to write.
 *
 * `remembered` is the place the reader's own repo already holds, and it is the reason
 * this is not simply a reading of the session: a sitting opens in the middle of a
 * document and reads four pages of it, so the session knows four where the record knows
 * nine — and the record it is about to overwrite is the only thing that knows the other
 * five. So the pages are *unioned* with what was there, and the two counts only ever
 * move up. Without it a reader who came back to carry on would watch their progress
 * reset with every visit, which is the one thing a resume must never do.
 *
 * `null` for a read that has brought a page into view nowhere: a place with no page is
 * not a place, and writing one would make the next device fetch, parse and dismiss a
 * record that says nothing.
 */
export function documentPlaceFromRead(
  session: DocumentPlaySession,
  {
    remembered,
    now = new Date().toISOString(),
  }: {remembered?: DocumentPlace | null; now?: string} = {},
): DocumentPlace | null {
  const page = stoppedPageOf(session) ?? remembered?.page
  if (page === undefined) return null
  const counts = documentReadCounts(session)
  const pages = mergeDocumentPages(
    remembered?.pages,
    documentReadPages(session),
  )
  return {
    page,
    pages,
    pagesSeen: Math.max(pages.length, remembered?.pagesSeen ?? 0),
    answered: Math.max(counts.answered, remembered?.answered ?? 0),
    updatedAt: now,
  }
}

/**
 * A place to write, given the newer one in hand and the older one in the repo.
 *
 * The last guarantee before the network, and the one the union in
 * `documentPlaceFromRead` cannot make on its own: a caller may build a place from a
 * session alone — answering a question does exactly that — and the place it hands over
 * knows only this sitting. Here the two are reconciled on one rule: *where* the reader
 * is comes from the newer place, and *how much they have done* can only grow, because
 * the older place is a record of pages this sitting never saw.
 */
export function mergeDocumentPlaces(
  place: DocumentPlace,
  remembered?: DocumentPlace | null,
): DocumentPlace {
  if (!remembered) return place
  const pages = mergeDocumentPages(remembered.pages, place.pages)
  return {
    page: place.page,
    pages,
    pagesSeen: Math.max(pages.length, place.pagesSeen, remembered.pagesSeen),
    answered: Math.max(place.answered, remembered.answered),
    updatedAt: place.updatedAt,
  }
}

/** The record for a place, ready to be written to the reader's repo. */
export function documentProgressRecordFor(
  place: DocumentPlace,
  {documentUri, documentTitle}: {documentUri: string; documentTitle?: string},
): DocumentProgressRecord | null {
  if (!(place.page > 0)) return null
  return {
    $type: DOCUMENT_PROGRESS_COLLECTION,
    documentUri,
    documentTitle,
    updatedAt: place.updatedAt,
    page: place.page,
    pages: place.pages,
    pagesSeen: place.pagesSeen,
    answered: place.answered,
  }
}

/**
 * The place a record holds — or `null` when it holds nothing usable.
 *
 * The document check is not politeness: `getRecord` answers whatever lives at the key
 * it was given, and a record is also readable by its author on another build of this
 * app. A place that names a different document is a place in a different document,
 * and adopting it would open the reader in the middle of something else.
 *
 * The counts are clamped rather than discarded: a record whose `pagesSeen` is absurd
 * is still a record of a page, and a reader is better served by "carry on from page 9"
 * with an odd count beside it than by no offer at all. The page itself is the one
 * number that is *required* — without it there is no place.
 *
 * A record with a list of pages is read *off the list*, because the list is what earlier
 * sittings actually did; the count beside it is kept as a floor, since a list can be
 * shorter than the reading that wrote it (the list is bounded, and a count may have been
 * written before there were lists at all).
 */
export function documentPlaceFromRecord(
  value: unknown,
  documentUri: string,
): DocumentPlace | null {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  if (record.documentUri !== documentUri) return null
  const page = wholeNumber(record.page)
  if (page === 0) return null
  const pages = mergeDocumentPages(
    Array.isArray(record.pages) ? record.pages : [],
  )
  const counted = wholeNumber(record.pagesSeen)
  return {
    page,
    pages,
    /*
     * A read cannot have seen fewer pages than the page it stopped on when nothing else
     * says otherwise, and saying so keeps a card from printing "page 9 · 2 pages read".
     * With a list, the list is the truth and the page is no floor for it: a reader who
     * jumped to page 20 has read one page, and "page 20 · 1 page read" is honest.
     */
    pagesSeen:
      pages.length > 0
        ? Math.max(pages.length, counted)
        : Math.max(page, counted),
    answered: wholeNumber(record.answered),
    updatedAt: typeof record.updatedAt === 'string' ? record.updatedAt : '',
  }
}

/** A non-negative whole number, or 0 — nothing here can be a fraction or a NaN. */
function wholeNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.trunc(value)
    : 0
}
